import React, { useState } from 'react';
import brand from '../brand';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const { showSuccess } = useToast();
  const { colors } = useTheme();
  // Read synchronously from the baked-in brand: fetching it here made the
  // login page show ZONE and then switch to the real name a beat later.
  const navigate = useNavigate();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      showSuccess(`Welcome back, ${user.firstName}!`);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: colors.black,
        p: 2
      }}
    >
      <Box 
        sx={{ 
          maxWidth: 360, 
          width: '100%'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: colors.blackCard,
            border: `1px solid ${colors.borderDark}`,
            borderBottom: 'none',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Box
            sx={{
              width: 3,
              height: 32,
              bgcolor: colors.accent,
            }}
          />
          <Box>
            {brand.logoUrl ? (
              // The sign-in screen is the one place the brand should actually
              // be shown, and it was the one place showing only text.
              <Box
                component="img"
                src={brand.logoUrl}
                alt={brand.companyName}
                sx={{ height: 34, maxWidth: 260, display: 'block', objectFit: 'contain', objectPosition: 'left center' }}
              />
            ) : (
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  color: colors.textWhite,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  lineHeight: 1
                }}
              >
                {brand.productName}
              </Typography>
            )}
            <Typography 
              sx={{ 
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.5625rem',
                color: colors.textDarkSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                mt: 0.5
              }}
            >
              Business Management
            </Typography>
          </Box>
        </Box>

        {/* Form */}
        <Box
          sx={{
            bgcolor: colors.blackCard,
            border: `1px solid ${colors.borderDark}`,
            p: 2.5
          }}
        >
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: colors.statusRed,
                '& .MuiAlert-icon': {
                  color: colors.statusRed
                }
              }}
            >
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem' }}>
                {error}
              </Typography>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2 }}>
              <Typography
                component="label"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.5625rem',
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  display: 'block',
                  mb: 0.75
                }}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                size="small"
                placeholder="Enter email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: colors.black,
                    '& input': {
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.8125rem',
                      py: 1,
                      '&::placeholder': {
                        color: colors.borderMuted,
                        opacity: 1
                      }
                    }
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: 2.5 }}>
              <Typography
                component="label"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.5625rem',
                  fontWeight: 600,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  display: 'block',
                  mb: 0.75
                }}
              >
                Password
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                size="small"
                placeholder="Enter password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: colors.textDarkSecondary }}
                      >
                        {showPassword ? <VisibilityOff sx={{ fontSize: '1rem' }} /> : <Visibility sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: colors.black,
                    '& input': {
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.8125rem',
                      py: 1,
                      '&::placeholder': {
                        color: colors.borderMuted,
                        opacity: 1
                      }
                    }
                  }
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ 
                py: 1,
                fontSize: '0.6875rem'
              }}
            >
              {loading ? (
                <CircularProgress size={16} sx={{ color: colors.textWhite }} />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            mt: 2,
            textAlign: 'center'
          }}
        >
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.5rem',
              color: colors.borderMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}
          >
            {brand.companyName} &copy; {new Date().getFullYear()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
